import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, Button, Alert, Form, Table, Row, Col, Collapse, Pagination, Dropdown, InputGroup, Spinner } from 'react-bootstrap';
import { Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getCustomerExtractionJobs, archiveExtractionJob, unarchiveExtractionJob, getExtractionJobFile, getExtractionResults, retryExtractionJob, compareExtractionResults, forceReprocessExtractionJob, exportExtractionJob, retryExportExtractionJob, markAsUnprocessable } from '../services/api/extractionApi';
import { getCustomersForDropdown } from '../services/api/customerApi';
import { getMetadataFieldsByCustomer } from '../services/api/metadataApi';
import { getRulesetTypesByCustomer } from '../services/api/rulesetApi';
import { formatMetadataValue, getColumnWidth } from '../utils/metadataUtils';
import { useNavigation } from '../contexts/NavigationContext';
import { EXTRACTION_JOB_STATUS, STATUS_DISPLAY, STATUS_VARIANTS } from '../constants/statusConstants';
import ExtractionDetailsView from './ExtractionDetailsView';
import useBulkSelection from '../hooks/useBulkSelection';
import BulkActionToolbar from './shared/BulkActionToolbar';
import BulkConfirmationModal from './shared/BulkConfirmationModal';
import { processBatch, getIneligibleJobs } from '../utils/bulkActionUtils';
import { usePageBreadcrumbs } from '../hooks/usePageBreadcrumbs';


const ExtractionJobsList = () => {
  usePageBreadcrumbs([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filter, setFilter] = useState('ALL');
  const [showArchived, setShowArchived] = useState(false);
  const [metadataFields, setMetadataFields] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');

  const [rulesets, setRulesets] = useState([]);
  const [selectedRuleset, setSelectedRuleset] = useState('');
  const [expandedJobId, setExpandedJobId] = useState(null);
  const [expandedJobData, setExpandedJobData] = useState(null);
  const [expandedComparison, setExpandedComparison] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingExpanded, setLoadingExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('UpdatedAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalRows, setTotalRows] = useState(0);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [debouncedFilterModel, setDebouncedFilterModel] = useState({ items: [] });
  const [localFilters, setLocalFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    id: true,
    fileName: true,
    status: true,
    createdAt: true,
    updatedAt: true,
    processingTime: true
  });

  const { extractionListFilters, clearExtractionFilters } = useNavigation();

  // Bulk selection hook
  const {
    selectedIds,
    isAllSelected,
    isIndeterminate,
    selectedCount,
    toggleItem,
    toggleAll,
    clearSelection,
  } = useBulkSelection(jobs);

  // Bulk operation state
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);
  const [bulkSummary, setBulkSummary] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    actionConfig: null,
    eligibleItems: [],
    ineligibleItems: [],
  });

  // Header checkbox ref for indeterminate state
  const headerCheckboxRef = useRef(null);

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  // Status display function for ineligible job reasons
  const statusDisplayFn = (job) => `Current status: ${STATUS_DISPLAY[job.statusId] || 'Unknown'}`;

  // Bulk action configurations
  const bulkActionConfigs = useMemo(() => [
    {
      name: 'retryExtraction',
      label: 'Retry Extraction',
      variant: 'outline-primary',
      icon: 'bi-arrow-clockwise',
      eligibilityFilter: (job) =>
        [EXTRACTION_JOB_STATUS.FAILED, EXTRACTION_JOB_STATUS.UNPROCESSABLE].includes(job.statusId),
      apiCall: retryExtractionJob,
    },
    {
      name: 'forceReprocess',
      label: 'Force Reprocess',
      variant: 'outline-warning',
      icon: 'bi-arrow-repeat',
      eligibilityFilter: (job) => job.statusId === EXTRACTION_JOB_STATUS.PROCESSING,
      apiCall: forceReprocessExtractionJob,
    },
    {
      name: 'export',
      label: 'Export',
      variant: 'outline-success',
      icon: 'bi-box-arrow-up',
      eligibilityFilter: (job) => job.statusId === EXTRACTION_JOB_STATUS.READY_FOR_EXPORT,
      apiCall: exportExtractionJob,
    },
    {
      name: 'retryExport',
      label: 'Retry Export',
      variant: 'outline-info',
      icon: 'bi-arrow-clockwise',
      eligibilityFilter: (job) =>
        [EXTRACTION_JOB_STATUS.EXPORT_FAILED, EXTRACTION_JOB_STATUS.EXPORTED, EXTRACTION_JOB_STATUS.EXPORTING].includes(job.statusId),
      apiCall: retryExportExtractionJob,
    },
    {
      name: 'markUnprocessable',
      label: 'Mark Unprocessable',
      variant: 'outline-dark',
      icon: 'bi-x-circle',
      eligibilityFilter: () => true,
      requiresReason: true,
      apiCall: markAsUnprocessable,
    },
    {
      name: 'archive',
      label: 'Archive',
      variant: 'outline-secondary',
      icon: 'bi-archive',
      eligibilityFilter: (job) => !job.isArchived,
      apiCall: archiveExtractionJob,
      showWhen: (archived) => !archived,
    },
    {
      name: 'unarchive',
      label: 'Unarchive',
      variant: 'outline-success',
      icon: 'bi-archive',
      eligibilityFilter: (job) => job.isArchived,
      apiCall: unarchiveExtractionJob,
      showWhen: (archived) => archived,
    },
  ], []);

  // Get selected job objects
  const selectedItems = useMemo(
    () => jobs.filter((job) => selectedIds.has(job.id)),
    [jobs, selectedIds]
  );

  // Filter actions based on showArchived state
  const visibleBulkActions = useMemo(
    () =>
      bulkActionConfigs
        .filter((action) => !action.showWhen || action.showWhen(showArchived))
        .map((action) => ({
          ...action,
          onAction: (eligibleItems) => {
            const ineligibleItems = getIneligibleJobs(selectedItems, action.eligibilityFilter, statusDisplayFn);
            setConfirmModal({
              show: true,
              actionConfig: action,
              eligibleItems,
              ineligibleItems,
            });
          },
        })),
    [bulkActionConfigs, showArchived, selectedItems]
  );

  // Bulk confirm handler
  const handleBulkConfirm = async (reason) => {
    const { actionConfig, eligibleItems } = confirmModal;
    setConfirmModal({ show: false, actionConfig: null, eligibleItems: [], ineligibleItems: [] });
    setBulkLoading(true);
    setBulkProgress(null);
    setBulkSummary(null);

    const result = await processBatch(
      eligibleItems,
      actionConfig.apiCall,
      reason,
      (progress) => setBulkProgress(progress)
    );

    setBulkLoading(false);
    setBulkSummary({
      successCount: result.success,
      failedCount: result.failed,
      errors: result.errors,
    });
    clearSelection();
    fetchJobs();
  };

  // Bulk cancel handler
  const handleBulkCancel = () => {
    setConfirmModal({ show: false, actionConfig: null, eligibleItems: [], ineligibleItems: [] });
  };

  const getStatusBadge = (status) => {
    const variant = STATUS_VARIANTS[status] || 'secondary';
    const text = STATUS_DISPLAY[status] || status;
    return <Badge bg={variant}>{text}</Badge>;
  };

  const fetchMetadataFields = useCallback(async (customerId) => {
    if (!customerId) {
      setMetadataFields([]);
      return;
    }
    try {
      const response = await getMetadataFieldsByCustomer(customerId);
  
      setMetadataFields(response.data || []);
    } catch (error) {
      console.error('Error fetching metadata fields:', error);
    }
  }, []);

  const fetchRulesets = useCallback(async (customerId) => {
    if (!customerId) {
      setRulesets([]);
      return;
    }
    try {
      const response = await getRulesetTypesByCustomer(customerId);
      setRulesets(response.data || []);
    } catch (error) {
      console.error('Error fetching rulesets:', error);
    }
  }, []);

  const fetchJobs = useCallback(async () => {
    if (!selectedCustomer) {
      setJobs([]);
      setTotalRows(0);
      return;
    }
    setLoading(true);
    try {
      // Build sort model for server
      const sortModel = sortBy ? [{ field: sortBy, sort: sortOrder }] : [];
      
      const response = await getCustomerExtractionJobs(
        filter !== 'ALL' ? filter : null, 
        showArchived, 
        selectedCustomer,
        null,
        currentPage,
        pageSize,
        sortModel,
        debouncedFilterModel,
        selectedRuleset || null
      );
      const jobsData = response.data?.items || [];
      setJobs(jobsData);
      setTotalRows(response.data?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching extraction jobs:', error);
      setError('Failed to load extraction jobs');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  }, [filter, showArchived, selectedCustomer, selectedRuleset, currentPage, pageSize, sortBy, sortOrder, debouncedFilterModel]);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await getCustomersForDropdown();
      setCustomers(response.data.items || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (selectedCustomer) {
      fetchMetadataFields(selectedCustomer);
      fetchRulesets(selectedCustomer);
    } else {
      setMetadataFields([]);
      setRulesets([]);
    }
  }, [selectedCustomer]);

  // Handle navigation context filters and restore from localStorage
  useEffect(() => {
    if (extractionListFilters.customerId) {
      setSelectedCustomer(extractionListFilters.customerId);
      setSelectedRuleset(extractionListFilters.rulesetId || '');
      setFilter(extractionListFilters.status || 'ALL');
      // Clear localStorage when new navigation filters are applied
      localStorage.removeItem('extractionListFilters');
      // Clear the filters after applying them
      clearExtractionFilters();
    } else {
      // Restore from localStorage if no navigation filters
      const savedFilters = localStorage.getItem('extractionListFilters');
      if (savedFilters) {
        const { customerId, rulesetId, status } = JSON.parse(savedFilters);
        setSelectedCustomer(customerId || '');
        setSelectedRuleset(rulesetId || '');
        setFilter(status || 'ALL');
      }
    }
  }, [extractionListFilters, clearExtractionFilters]);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    if (selectedCustomer || selectedRuleset || filter !== 'ALL') {
      localStorage.setItem('extractionListFilters', JSON.stringify({
        customerId: selectedCustomer,
        rulesetId: selectedRuleset,
        status: filter
      }));
    }
  }, [selectedCustomer, selectedRuleset, filter]);



  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page on search
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Debounce filter model
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilterModel(prev => {
        // Only update if the content actually changed
        if (JSON.stringify(prev) !== JSON.stringify(filterModel)) {
          setCurrentPage(1); // Reset to first page on filter
          return filterModel;
        }
        return prev;
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [filterModel]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchJobs();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [filter, showArchived, selectedCustomer, selectedRuleset, currentPage, pageSize, sortBy, sortOrder, debouncedFilterModel]);

  const handleArchive = async (jobId) => {
    try {
      const response = await archiveExtractionJob(jobId);
      console.log('Archive response:', response);
      
      // Defensive handling of response structure
      if (!response) {
        throw new Error('No response received from server');
      }
      
      // Handle both response.data and direct response structures
      const responseData = response.data || response;
      
      // Check if the operation was successful
      if (responseData.success === false) {
        throw new Error(responseData.message || 'Archive operation failed');
      }
      
      setSuccess('Job archived successfully');
      setTimeout(() => setSuccess(''), 3000);
      fetchJobs();
    } catch (error) {
      console.error('Error archiving job:', error);
      console.error('Error response:', error.response);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to archive job';
      setError(errorMessage);
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleUnarchive = async (jobId) => {
    try {
      const response = await unarchiveExtractionJob(jobId);
      console.log('Unarchive response:', response);
      
      // Defensive handling of response structure
      if (!response) {
        throw new Error('No response received from server');
      }
      
      // Handle both response.data and direct response structures
      const responseData = response.data || response;
      
      // Check if the operation was successful
      if (responseData.success === false) {
        throw new Error(responseData.message || 'Unarchive operation failed');
      }
      
      setSuccess('Job restored successfully');
      setTimeout(() => setSuccess(''), 3000);
      fetchJobs();
    } catch (error) {
      console.error('Error unarchiving job:', error);
      console.error('Error response:', error.response);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to unarchive job';
      setError(errorMessage);
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleRetryJob = async (jobId) => {
    try {
      await retryExtractionJob(jobId);
      fetchJobs(); // Refresh the job list to show updated status
    } catch (error) {
      console.error('Error retrying job:', error);
      setError('Failed to retry job: ' + (error.response?.data || error.message));
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleJobUpdate = useCallback(async () => {
    fetchJobs();
    // Also refresh the expanded job data so lineItemsEditable and other flags update
    if (expandedJobId) {
      try {
        const jobResponse = await getExtractionResults(expandedJobId);
        setExpandedJobData(jobResponse.data);
      } catch (err) {
        console.error('Error refreshing expanded job:', err);
      }
    }
  }, [fetchJobs, expandedJobId]);

  const handleExpandJob = useCallback(async (jobId) => {
    if (expandedJobId === jobId) {
      setExpandedJobId(null);
      setExpandedJobData(null);
      setExpandedComparison(null);
      setPdfUrl(null);
      return;
    }

    setExpandedJobData(null);
    setExpandedComparison(null);
    setPdfUrl(null);
    setExpandedJobId(jobId);
    setLoadingExpanded(true);
    
    try {
      // Fetch job data and comparison data in parallel
      const [jobResponse, comparisonResponse] = await Promise.all([
        getExtractionResults(jobId),
        compareExtractionResults(jobId).catch(err => {
          console.error('Error fetching comparison data:', err);
          return null; // Don't fail if comparison data is unavailable
        })
      ]);
      
      const jobData = jobResponse.data;
      setExpandedJobData(jobData);
      
      if (comparisonResponse?.data) {
        setExpandedComparison(comparisonResponse.data);
      }
      
      try {
        const response = await getExtractionJobFile(jobId);
        if (response.data && response.data.url) {
          setPdfUrl(response.data.url);
        } else {
           // Validate content type before creating blob
          const contentType = response.headers['content-type'];
          if (!contentType || !contentType.startsWith('application/pdf')) {
            console.error('Invalid file type received');
            return;
          }
          const fileBlob = new Blob([response.data], { type: response.headers['content-type'] });
          const fileUrl = URL.createObjectURL(fileBlob);
          setPdfUrl(fileUrl);
        }
      } catch (error) {
        console.error('Error fetching PDF:', error);
      }
    } catch (error) {
      console.error('Error fetching job details:', error);
    } finally {
      setLoadingExpanded(false);
    }
  }, [expandedJobId]);





  // Server-side filtering and search - jobs are already filtered by server
  const filteredJobs = jobs;

  // Server-side pagination calculation
  const totalPages = Math.ceil(totalRows / pageSize);
  
  // Handle sort changes
  const handleSort = (field) => {
    // Map frontend field names to backend property names
    const fieldMapping = {
      'createdAt': 'CreatedAt',
      'updatedAt': 'UpdatedAt',
      'fileName': 'FileName',
      'status': 'Status',
      'id': 'Id'
    };
    
    const backendField = fieldMapping[field] || field;
    
    if (sortBy === backendField) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(backendField);
      setSortOrder('asc');
    }
    setCurrentPage(1); // Reset to first page on sort
  };
  
  // Handle page size changes
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(Number(newPageSize));
    setCurrentPage(1); // Reset to first page
  };
  
  // Handle local filter changes (don't send to server yet)
  const handleLocalFilter = (field, operator, value) => {
    setLocalFilters(prev => ({
      ...prev,
      [field]: { operator, value }
    }));
  };
  
  // Update server filter model only when value is provided
  const updateServerFilter = (field, operator, value) => {
    // Map frontend field names to backend property names
    const fieldMapping = {
      'createdAt': 'CreatedAt',
      'updatedAt': 'UpdatedAt',
      'fileName': 'FileName',
      'status': 'Status',
      'id': 'Id'
    };
    
    const backendField = fieldMapping[field] || field;
    
    setFilterModel(prev => {
      const newItems = prev.items.filter(item => item.field !== backendField);
      if (operator && value) { // Only send to server when both operator and value exist
        newItems.push({ field: backendField, operator, value });
      }
      return { items: newItems };
    });
  };
  
  // Clear column filter
  const clearColumnFilter = (field) => {
    // Map frontend field names to backend property names
    const fieldMapping = {
      'createdAt': 'CreatedAt',
      'updatedAt': 'UpdatedAt',
      'fileName': 'FileName',
      'status': 'Status',
      'id': 'Id'
    };
    
    const backendField = fieldMapping[field] || field;
    const metadataField = `metadata_${field}`;
    
    setLocalFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[field];
      return newFilters;
    });
    setFilterModel(prev => ({
      items: prev.items.filter(item => item.field !== backendField && item.field !== metadataField)
    }));
  };
  
  // Get current local filter for field
  const getFieldFilter = (field) => {
    return localFilters[field] || { operator: '', value: '' };
  };
  
  // Get filter operators for field type
  const getFilterOperators = (field) => {
    if (field === 'createdAt' || field === 'updatedAt') {
      return [
        { value: 'equals', label: 'Is' },
        { value: 'not', label: 'Is not' },
        { value: 'after', label: 'Is after' },
        { value: 'before', label: 'Is before' }
      ];
    }
    if ([ 'processingTime'].includes(field)) {
      return [
        { value: 'equals', label: 'Equals' },
        { value: 'greaterThan', label: 'Greater than' },
        { value: 'lessThan', label: 'Less than' }
      ];
    }
    return [
      { value: 'contains', label: 'Contains' },
      { value: 'equals', label: 'Equals' },
      { value: 'startsWith', label: 'Starts with' },
      { value: 'endsWith', label: 'Ends with' }
    ];
  };



  return (
    <Card className="mb-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Extraction Jobs</h5>
        <Button variant="outline-primary" onClick={fetchJobs}>
          <i className="bi bi-arrow-clockwise me-1"></i> Refresh
        </Button>
      </Card.Header>
      <Card.Body>
        {success && <Alert variant="success">{success}</Alert>}
        {error && <Alert variant="danger">{error}</Alert>}
        
        <div className="d-flex justify-content-between align-items-end mb-3 gap-3">
          <Form.Group style={{ width: '200px' }}>
            <Form.Label>Customer</Form.Label>
            <Form.Select 
              value={selectedCustomer}
              onChange={(e) => {
                const newCustomerId = e.target.value;
                setSelectedCustomer(newCustomerId);
                setSelectedRuleset(''); // Reset ruleset when customer changes
                if (!newCustomerId) {
                  localStorage.removeItem('extractionListFilters');
                }
              }}
            >
              <option value="">Select Customer</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.customerName}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          
          <Form.Group style={{ width: '200px' }}>
            <Form.Label>Extraction Rule Set</Form.Label>
            <Form.Select 
              value={selectedRuleset}
              onChange={(e) => setSelectedRuleset(e.target.value)}
              disabled={!selectedCustomer}
            >
              <option value="">All Rule Sets</option>
              {rulesets.map(ruleset => (
                <option key={ruleset.id} value={ruleset.id}>
                  {ruleset.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          
          <Form.Group style={{ width: '200px' }}>
            <Form.Label>Status</Form.Label>
            <Form.Select 
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              disabled={!selectedCustomer}
            >
              <option value="ALL">All Jobs</option>
              <option value={EXTRACTION_JOB_STATUS.REQUESTED}>Requested</option>
              <option value={EXTRACTION_JOB_STATUS.PROCESSING}>Processing</option>
              <option value={EXTRACTION_JOB_STATUS.COMPLETED}>Completed</option>
              <option value={EXTRACTION_JOB_STATUS.NEEDS_REVIEW}>Needs Review</option>
              <option value={EXTRACTION_JOB_STATUS.FAILED}>Failed</option>
              <option value={EXTRACTION_JOB_STATUS.EXPORTING}>Exporting</option>
              <option value={EXTRACTION_JOB_STATUS.EXPORTED}>Exported</option>
              <option value={EXTRACTION_JOB_STATUS.EXPORT_FAILED}>Export Failed</option>
              <option value={EXTRACTION_JOB_STATUS.UNPROCESSABLE}>Unprocessable</option>
              <option value={EXTRACTION_JOB_STATUS.AUGMENTING}>Augmenting</option>
              <option value={EXTRACTION_JOB_STATUS.AUGMENTATION_REVIEW}>Augmentation Review</option>
              <option value={EXTRACTION_JOB_STATUS.AUGMENTATION_FAILED}>Augmentation Failed</option>
              <option value={EXTRACTION_JOB_STATUS.READY_FOR_EXPORT}>Ready for Export</option>
            </Form.Select>
          </Form.Group>
          
          <Form.Check 
            type="switch"
            id="show-archived"
            label="Show Archived"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
        </div>
        
        {!selectedCustomer ? (
          <div className="text-center my-5">
            <p>Please select a customer to view extraction jobs.</p>
          </div>
        ) : (
          <div>
            {/* Enhanced Toolbar */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex gap-3 align-items-center">
                <Dropdown show={showColumnSelector} onToggle={setShowColumnSelector}>
                  <Dropdown.Toggle variant="outline-secondary" size="sm">
                    <i className="bi bi-columns-gap me-1"></i> Columns
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {Object.entries(visibleColumns).map(([key, visible]) => (
                      <Dropdown.Item key={key} as="div" onClick={(e) => e.stopPropagation()}>
                        <Form.Check
                          type="checkbox"
                          id={`col-${key}`}
                          label={key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                          checked={visible}
                          onChange={(e) => {
                            e.stopPropagation();
                            setVisibleColumns(prev => ({ ...prev, [key]: e.target.checked }));
                          }}
                        />
                      </Dropdown.Item>
                    ))}
                    {metadataFields.length > 0 && <Dropdown.Divider />}
                    {metadataFields.map(field => (
                      <Dropdown.Item key={`metadata-${field.id}`} as="div" onClick={(e) => e.stopPropagation()}>
                        <Form.Check
                          type="checkbox"
                          id={`col-metadata-${field.id}`}
                          label={field.name}
                          checked={visibleColumns[`metadata_${field.id}`] !== false}
                          onChange={(e) => {
                            e.stopPropagation();
                            setVisibleColumns(prev => ({ ...prev, [`metadata_${field.id}`]: e.target.checked }));
                          }}
                        />
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
                
                <Button 
                  variant={showFilters ? "primary" : "outline-secondary"} 
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <i className="bi bi-funnel me-1"></i> Filters
                  {Object.values(localFilters).filter(f => f.operator && f.value?.trim()).length > 0 && (
                    <span className="badge bg-danger ms-1">{Object.values(localFilters).filter(f => f.operator && f.value?.trim()).length}</span>
                  )}
                </Button>
              </div>
              
              <div className="d-flex gap-2 align-items-center">
                <span className="text-muted">Rows per page:</span>
                <Form.Select 
                  size="sm" 
                  style={{ width: 'auto' }}
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(e.target.value)}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Form.Select>
              </div>
            </div>

            {/* Column Filters */}
            {showFilters && (
              <Card className="mb-3">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">Column Filters</h6>
                    <Button 
                      variant="outline-danger" 
                      size="sm"
                      onClick={() => {
                        setLocalFilters({});
                        setFilterModel({ items: [] });
                        setDebouncedFilterModel({ items: [] });
                      }}
                      disabled={Object.values(localFilters).filter(f => f.operator && f.value?.trim()).length === 0}
                    >
                      Clear All
                    </Button>
                  </div>
                  <Row>
                    {Object.entries(visibleColumns)
                      .filter(([key, visible]) => visible && !['processingTime'].includes(key))
                      .map(([field]) => (
                      <Col md={4} key={field} className="mb-2">
                        <Form.Label className="small fw-bold">
                          {field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')}
                        </Form.Label>
                        <div className="d-flex gap-1">
                          <select 
                            className="form-select form-select-sm"
                            style={{ width: '120px' }}
                            value={getFieldFilter(field)?.operator || ''}
                            onChange={(e) => {
                              const selectedOperator = e.target.value;
                              if (selectedOperator) {
                                handleLocalFilter(field, selectedOperator, getFieldFilter(field).value || '');
                              } else {
                                clearColumnFilter(field);
                              }
                            }}
                          >
                            <option value="">No filter</option>
                            {getFilterOperators(field).map(op => (
                              <option key={op.value} value={op.value}>{op.label}</option>
                            ))}
                          </select>
                          <input
                            className="form-control form-control-sm"
                            type={field === 'createdAt' || field === 'updatedAt' ? 'date' : 'text'}
                            placeholder={field === 'createdAt' || field === 'updatedAt' ? 'YYYY-MM-DD' : 'Value...'}
                            value={getFieldFilter(field)?.value || ''}
                            onChange={(e) => {
                              const currentFilter = getFieldFilter(field);
                              const newValue = e.target.value;
                              if (currentFilter.operator) {
                                handleLocalFilter(field, currentFilter.operator, newValue);
                                // Only send to server when value is entered
                                if (newValue.trim()) {
                                  updateServerFilter(field, currentFilter.operator, newValue);
                                } else {
                                  // Remove from server filter if value is empty
                                  const fieldMapping = {
                                    'createdAt': 'CreatedAt',
                                    'updatedAt': 'UpdatedAt',
                                    'fileName': 'FileName',
                                    'status': 'Status',
                                    'id': 'Id'
                                  };
                                  const backendField = fieldMapping[field] || field;
                                  setFilterModel(prev => ({
                                    items: prev.items.filter(item => item.field !== backendField)
                                  }));
                                }
                              }
                            }}
                            disabled={!getFieldFilter(field).operator}
                          />
                          {getFieldFilter(field) && (
                            <Button 
                              variant="outline-danger" 
                              size="sm"
                              onClick={() => clearColumnFilter(field)}
                            >
                              ×
                            </Button>
                          )}
                        </div>
                      </Col>
                    ))}
                    {metadataFields.map(field => (
                      <Col md={4} key={`metadata-${field.id}`} className="mb-2">
                        <Form.Label className="small fw-bold">
                          {field.name}
                        </Form.Label>
                        <div className="d-flex gap-1">
                          <select 
                            className="form-select form-select-sm"
                            style={{ width: '120px' }}
                            value={getFieldFilter(field.fieldKey)?.operator || ''}
                            onChange={(e) => {
                              const selectedOperator = e.target.value;
                              if (selectedOperator) {
                                handleLocalFilter(field.fieldKey, selectedOperator, getFieldFilter(field.fieldKey).value || '');
                              } else {
                                clearColumnFilter(field.fieldKey);
                                setFilterModel(prev => ({
                                  items: prev.items.filter(item => item.field !== `metadata_${field.fieldKey}`)
                                }));
                              }
                            }}
                          >
                            <option value="">No filter</option>
                            {getFilterOperators('string').map(op => (
                              <option key={op.value} value={op.value}>{op.label}</option>
                            ))}
                          </select>
                          <input
                            className="form-control form-control-sm"
                            type="text"
                            placeholder="Value..."
                            value={getFieldFilter(field.fieldKey)?.value || ''}
                            onChange={(e) => {
                              const currentFilter = getFieldFilter(field.fieldKey);
                              const newValue = e.target.value;
                              if (currentFilter.operator) {
                                handleLocalFilter(field.fieldKey, currentFilter.operator, newValue);
                                if (newValue.trim()) {
                                  updateServerFilter(`metadata_${field.fieldKey}`, currentFilter.operator, newValue);
                                } else {
                                  setFilterModel(prev => ({
                                    items: prev.items.filter(item => item.field !== `metadata_${field.fieldKey}`)
                                  }));
                                }
                              }
                            }}
                            disabled={!getFieldFilter(field.fieldKey).operator}
                          />
                          {getFieldFilter(field.fieldKey) && (
                            <Button 
                              variant="outline-danger" 
                              size="sm"
                              onClick={() => clearColumnFilter(field.fieldKey)}
                            >
                              ×
                            </Button>
                          )}
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Card.Body>
              </Card>
            )}

            {/* Bulk Action Toolbar */}
            {selectedCount > 0 && (
              <div className="mb-3">
                <div className="d-flex align-items-center mb-2">
                  <Badge bg="primary" className="me-2">{selectedCount} selected</Badge>
                </div>
                <BulkActionToolbar
                  actions={visibleBulkActions}
                  selectedItems={selectedItems}
                  loading={bulkLoading}
                  progress={bulkProgress}
                  summary={bulkSummary}
                  onDismissSummary={() => setBulkSummary(null)}
                />
              </div>
            )}

            {/* Summary when no selection (persists after clear) */}
            {selectedCount === 0 && bulkSummary && (
              <div className="mb-3">
                <BulkActionToolbar
                  actions={[]}
                  selectedItems={[]}
                  loading={false}
                  progress={null}
                  summary={bulkSummary}
                  onDismissSummary={() => setBulkSummary(null)}
                />
              </div>
            )}

            {/* Enhanced Table */}
            <div style={{ overflowX: 'auto' }}>
              <Table striped bordered hover style={{ minWidth: '1200px', tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      ref={headerCheckboxRef}
                      checked={isAllSelected}
                      onChange={toggleAll}
                      disabled={bulkLoading || jobs.length === 0}
                    />
                  </th>
                  <th style={{ width: '50px' }}></th>
                  {visibleColumns.id && (
                    <th 
                      style={{ cursor: 'pointer', width: '200px' }}
                      onClick={() => handleSort('id')}
                    >
                      Job ID {sortBy === 'Id' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                  )}
                  {visibleColumns.fileName && (
                    <th 
                      style={{ cursor: 'pointer', width: '250px' }}
                      onClick={() => handleSort('fileName')}
                    >
                      File Name {sortBy === 'FileName' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                  )}
                  {visibleColumns.status && (
                    <th 
                      style={{ cursor: 'pointer', width: '130px' }}
                      onClick={() => handleSort('status')}
                    >
                      Status {sortBy === 'Status' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                  )}
                  {visibleColumns.createdAt && (
                    <th 
                      style={{ cursor: 'pointer', width: '180px' }}
                      onClick={() => handleSort('createdAt')}
                    >
                      Created {sortBy === 'CreatedAt' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                  )}
                  {visibleColumns.updatedAt && (
                    <th 
                      style={{ cursor: 'pointer', width: '180px' }}
                      onClick={() => handleSort('updatedAt')}
                    >
                      Updated {sortBy === 'UpdatedAt' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                  )}

                  {visibleColumns.processingTime && (
                    <th 
                      style={{ cursor: 'pointer', width: '150px' }}
                      onClick={() => handleSort('totalProcessingTimeMs')}
                    >
                      Processing Time {sortBy === 'totalProcessingTimeMs' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                  )}
                  
                  {metadataFields.filter(field => visibleColumns[`metadata_${field.id}`] !== false).map(field => (
                    <th 
                      key={field.id} 
                      style={{ cursor: 'pointer', width: getColumnWidth(field.name, field.dataType) + 'px' }}
                      onClick={() => handleSort(`metadata_${field.fieldKey}`)}
                    >
                      {field.name} {sortBy === `metadata_${field.fieldKey}` && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                  ))}
                  <th style={{ width: '280px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.length === 0 ? (
                  <tr>
                    <td colSpan={Object.values(visibleColumns).filter(Boolean).length + metadataFields.filter(field => visibleColumns[`metadata_${field.id}`] !== false).length + 3} className="text-center py-4">
                      <div className="text-muted">
                        <i className="bi bi-inbox" style={{ fontSize: '2rem' }}></i>
                        <div className="mt-2">No extraction jobs found</div>
                        {(filter !== 'ALL' || selectedRuleset || Object.values(localFilters).some(f => f.operator && f.value?.trim())) && (
                          <div className="small">Try adjusting your filters</div>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  jobs.map((job) => (
                  <React.Fragment key={job.id}>
                    <tr>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(job.id)}
                          onChange={() => toggleItem(job.id)}
                          disabled={bulkLoading}
                        />
                      </td>
                      <td>
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => handleExpandJob(job.id)}
                          style={{ padding: 0 }}
                        >
                          {expandedJobId === job.id ? '▼' : '▶'}
                        </Button>
                      </td>
                      {visibleColumns.id && <td style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={job.id}>{job.id}</td>}
                      {visibleColumns.fileName && <td style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={job.fileName}>{job.fileName}</td>}
                      {visibleColumns.status && <td style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{getStatusBadge(job.statusId)}</td>}
                      {visibleColumns.createdAt && <td style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={new Date(job.createdAt).toLocaleString()}>{new Date(job.createdAt).toLocaleString()}</td>}
                      {visibleColumns.updatedAt && <td style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={new Date(job.updatedAt).toLocaleString()}>{new Date(job.updatedAt).toLocaleString()}</td>}
                      
                      {visibleColumns.processingTime && (
                        <td style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{job.totalProcessingTimeMs ? `${(job.totalProcessingTimeMs / 1000).toFixed(2)}s` : 'N/A'}</td>
                      )}
                      {metadataFields.filter(field => visibleColumns[`metadata_${field.id}`] !== false).map(field => (
                        <td key={field.id} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={formatMetadataValue(job.invoiceMetadata?.[field.fieldKey])}>
                          {formatMetadataValue(job.invoiceMetadata?.[field.fieldKey])}
                        </td>
                      ))}
                      <td>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <Link to={`/extraction-jobs/${job.id}`}>
                            <Button variant="outline-secondary" size="sm">
                              Details
                            </Button>
                          </Link>
                          {job.statusId === EXTRACTION_JOB_STATUS.FAILED && (
                            <Button 
                              variant="outline-primary" 
                              size="sm" 
                              onClick={() => handleRetryJob(job.id)}
                              title="Retry failed extraction"
                            >
                              <i className="bi bi-arrow-clockwise me-1"></i>
                              Retry
                            </Button>
                          )}
                          {job.isArchived ? (
                            <Button 
                              variant="outline-success" 
                              size="sm" 
                              onClick={() => handleUnarchive(job.id)}
                            >
                              Unarchive
                            </Button>
                          ) : (
                            <Button 
                              variant="outline-secondary" 
                              size="sm" 
                              onClick={() => handleArchive(job.id)}
                            >
                              Archive
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedJobId === job.id && (
                      <tr>
                        <td colSpan={Object.values(visibleColumns).filter(Boolean).length + metadataFields.filter(field => visibleColumns[`metadata_${field.id}`] !== false).length + 3} style={{ padding: 0 }}>
                          <div style={{
                            backgroundColor: '#f8f9fa',
                            borderLeft: '4px solid #0d6efd',
                            padding: '16px',
                            width: '100%',
                            boxSizing: 'border-box',
                            overflow: 'hidden'
                          }}>
                            <div style={{ width: '100%', overflowX: 'auto' }}>
                              {expandedJobData ? (
                                <ExtractionDetailsView 
                                  job={expandedJobData} 
                                  showPdfViewer={true}
                                  pdfUrl={pdfUrl}
                                  loadingPdf={loadingExpanded}
                                  onJobUpdate={handleJobUpdate}
                                  comparison={expandedComparison}
                                />
                              ) : (
                                <div className="text-center py-4">
                                  <Spinner animation="border" size="sm" />
                                  <p className="mt-2">Loading job details...</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  ))
                )}
              </tbody>
              </Table>
            </div>

            {/* Enhanced Pagination */}
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="text-muted">
                {totalRows === 0 ? (
                  'No entries found'
                ) : (
                  `Showing ${((currentPage - 1) * pageSize) + 1} to ${Math.min(currentPage * pageSize, totalRows)} of ${totalRows} entries`
                )}
              </div>
              {totalRows > 0 && (
                <Pagination>
                  <Pagination.First 
                    disabled={currentPage === 1 || totalPages === 0}
                    onClick={() => setCurrentPage(1)}
                  />
                  <Pagination.Prev 
                    disabled={currentPage === 1 || totalPages === 0}
                    onClick={() => setCurrentPage(prev => prev - 1)}
                  />
                  {totalPages > 0 && [...Array(Math.min(5, totalPages))].map((_, i) => {
                    const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                    if (page <= totalPages) {
                      return (
                        <Pagination.Item
                          key={page}
                          active={page === currentPage}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </Pagination.Item>
                      );
                    }
                    return null;
                  })}
                  <Pagination.Next 
                    disabled={currentPage === totalPages || totalPages === 0}
                    onClick={() => setCurrentPage(prev => prev + 1)}
                  />
                  <Pagination.Last 
                    disabled={currentPage === totalPages || totalPages === 0}
                    onClick={() => setCurrentPage(totalPages)}
                  />
                </Pagination>
              )}
            </div>
          </div>
        )}
      </Card.Body>

      {/* Bulk Confirmation Modal */}
      <BulkConfirmationModal
        show={confirmModal.show}
        actionName={confirmModal.actionConfig?.label || ''}
        eligibleItems={confirmModal.eligibleItems}
        ineligibleItems={confirmModal.ineligibleItems}
        requiresReason={confirmModal.actionConfig?.requiresReason || false}
        onConfirm={handleBulkConfirm}
        onCancel={handleBulkCancel}
      />
    </Card>
  );
};

export default ExtractionJobsList;